import React, { ReactNode } from "react";
import { Navigate } from "react-router";
import { usePermissions } from "src/context/MeContext";
import type { Scope } from "src/rbac/scopes";

interface RequireScopeProps {
  scope: Scope;
  children: ReactNode;
  redirectTo?: string;
}

/**
 * Redirects user if they don't have the required scope.
 * Used for protecting entire routes/pages.
 *
 * Usage:
 *   <RequireScope scope="read:knowledge">
 *     <KnowledgePageContent />
 *   </RequireScope>
 *
 *   <RequireScope scope="read:billing" redirectTo="/settings">
 *     <BillingPageContent />
 *   </RequireScope>
 */
export function RequireScope({ scope, children, redirectTo = "/chat" }: RequireScopeProps) {
  const { hasScope, scopes } = usePermissions();

  // Wait for scopes to be loaded before redirecting
  // This prevents race conditions during navigation where scopes might briefly be empty
  if (scopes.length === 0) {
    return null;
  }

  if (!hasScope(scope)) {
    return <Navigate to={redirectTo} replace />;
  }

  return <>{children}</>;
}
